/** @format */

import { AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import GobyMallPromoCard from "../components/AppPub";
import Banner from "../components/Banner";
import Categoys from "../components/Categoys";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import NosPartenaires from "../components/NosPartenaires";
import FutureProducts from "../components/products/FutureProducts";
import Products from "../components/products/Products";
import RecommendedProducts from "../components/products/RecommendedProducts";
import TopSellers from "../components/products/TopSellers";
import PromoBanner from "../components/PromoBanner";
import { get_products } from "../store/reducers/homeReducer";
import { useCategories } from "../utils/Categories";

// ─── Constants ────────────────────────────────────────────────────────────────

const PROMO_STORAGE_KEY = "gobyMallPromoSeen";
const PROMO_TTL_MS = 24 * 60 * 60 * 1000; // 24h before showing promo again

/** Returns true if the promo has NOT been recently seen */
const shouldShowPromo = () => {
  try {
    const seen = localStorage.getItem(PROMO_STORAGE_KEY);
    if (!seen) return true;
    return Date.now() - Number(seen) > PROMO_TTL_MS;
  } catch {
    return true;
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Overlay displayed while categories / products are loading.
 */
const LoadingOverlay = () => (
  <div
    role='status'
    aria-label='Chargement en cours'
    className='w-screen h-screen flex justify-center items-center fixed inset-0 bg-black/40 backdrop-blur-sm z-[99998]'
  >
    <FadeLoader color='#4F46E5' />
  </div>
);

/**
 * Non-blocking error banner.
 * @param {{ message: string }} props
 */
const ErrorBanner = ({ message }) => (
  <div className='w-[95%] mx-auto px-4 py-4' role='alert'>
    <div className='bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm font-medium'>
      {message}
    </div>
  </div>
);

/**
 * Reusable section title with decorative underline.
 * @param {{ title: string, subtitle?: string }} props
 */
const SectionHeader = ({ title, subtitle }) => (
  <div className='text-center mb-6'>
    <h2 className='text-sm sm:text-xl font-bold text-gray-900 tracking-tight uppercase'>
      {title}
    </h2>
    <div className='mt-3 mx-auto w-16 h-1 rounded-full bg-gradient-to-r from-indigo-600 to-blue-500' />
    {subtitle && (
      <p className='mt-3 text-sm text-gray-500 max-w-md mx-auto'>{subtitle}</p>
    )}
  </div>
);

/**
 * Side-by-side PromoBanner + RecommendedProducts section.
 * Adapts layout depending on which panel is present.
 */
const PromoAndPopularSection = ({
  hasPromoBanner,
  hasRecommendedProducts,
  popularProducts,
}) => {
  if (!hasPromoBanner && !hasRecommendedProducts) return null;

  const promoClass = hasRecommendedProducts ? "w-[25%] md-lg:w-full" : "w-full";
  const recommendedClass = hasPromoBanner ? "w-[75%] md-lg:w-full" : "w-full";

  return (
    <section
      aria-label='Promotions et produits populaires'
      className='py-4 flex px-4 mx-auto flex-row md-lg:flex-col gap-2 md-lg:gap-4 items-start'
    >
      {hasPromoBanner && (
        <div className={promoClass}>
          <div className=' sm:px-0'>
            <SectionHeader title='HOT DEALS' />
            <PromoBanner />
          </div>
        </div>
      )}

      {hasRecommendedProducts && (
        <div className={recommendedClass}>
          <div className='sm:px-4'>
            <SectionHeader title='LES PLUS POPULAIRES' />
            <RecommendedProducts products={popularProducts} />
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const Home = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    products,
    latestProducts,
    topRatedProducts,
    topDiscountedProducts,
    popularProducts,
    topOrderedProducts,
  } = useSelector((state) => state.home);

  const { categories, loading, errorMessage, clientLocation } = useCategories();

  // Lazy initializer avoids a localStorage read on every render
  const [showPromo, setShowPromo] = useState(shouldShowPromo);

  useEffect(() => {
    dispatch(get_products());
  }, [dispatch]);

  const handleClosePromo = useCallback(() => {
    try {
      localStorage.setItem(PROMO_STORAGE_KEY, String(Date.now()));
    } catch {
      // localStorage might be unavailable (private mode, etc.)
    }
    setShowPromo(false);
  }, []);

  // Derived flags — memoised to prevent downstream re-renders
  const hasRecommendedProducts = useMemo(
    () => Boolean(popularProducts?.length),
    [popularProducts],
  );

  const hasTopSellers = useMemo(
    () => Boolean(topOrderedProducts?.length),
    [topOrderedProducts],
  );

  return (
    <div className='w-full select-none font-helvetica bg-gray-50'>
      <Headers categories={categories} clientLocation={clientLocation} />

      {/* ── Hero ── */}
      <Banner />

      {/* ── Promo overlay ── */}
      <AnimatePresence>
        {showPromo && <GobyMallPromoCard onClose={handleClosePromo} />}
      </AnimatePresence>

      {/* ── Global loading overlay ── */}
      {loading && <LoadingOverlay />}

      <main className='justify-center items-center'>
        {/* ── Error ── */}
        {errorMessage && <ErrorBanner message={errorMessage} />}

        {/* ── Categories ── */}
        <section
          aria-label='Categories'
          className='py-4 justify-center items-center'
        >
          <Categoys categories={categories} />
        </section>

        <div className='justify-center items-end'>
          {/* ── Hot Deals + Popular ── */}
          <PromoAndPopularSection
            hasPromoBanner
            hasRecommendedProducts={hasRecommendedProducts}
            popularProducts={popularProducts}
          />

          {/* ── Top Sellers ── */}
          {hasTopSellers && (
            <section aria-label='Les plus vendus' className='py-4'>
              <div className='w-[95%] mx-auto px-4 sm:px-6 lg:px-4'>
                <SectionHeader title='LES PLUS VENDUS' />
                <TopSellers products={topOrderedProducts} />
              </div>
            </section>
          )}
        </div>

        {/* ── All products ── */}
        <section aria-label='Tous les produits' className='py-4 bg-white'>
          <FutureProducts products={products} />
        </section>

        {/* ── Nouveautes / Top Rated / Promo grid ── */}

        <section aria-label='Selections produits' className='py-10'>
          <div className='w-[95%] flex flex-wrap mx-auto'>
            <div className='grid w-full grid-cols-3 sm:grid-cols-1 gap-7 sm:gap-2'>
              <div className='overflow-hidden'>
                <Products title={t("New Products")} products={latestProducts} />
              </div>
              <div className='overflow-hidden'>
                <Products title={t("Top Rated")} products={topRatedProducts} />
              </div>
              <div className='overflow-hidden'>
                <Products
                  title={t("Discounted Products")}
                  products={topDiscountedProducts}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Partners ── */}
        <section aria-label='Nos partenaires' className='py-8 bg-white'>
          <NosPartenaires />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
